import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { formatRupiah, resolveFileUrl } from '../lib/api';
import Navbar from '../components/Navbar';
import TipModal from '../components/TipModal';
import { Coffee, Heart, Link as LinkIcon, Target, ArrowRight, Trophy, SpeakerHigh, ShieldCheck } from '@phosphor-icons/react';
import LoadingVideo from '../components/LoadingVideo';

export default function CreatorPublic() {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tipOpen, setTipOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [error, setError] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);

  const loadCreator = useCallback(async () => {
    try {
      const res = await api.get(`/creators/${username}`);
      setData(res.data);
    } catch {
      setError('Kreator tidak ditemukan');
    } finally {
      setLoading(false);
    }
  }, [username]);

  const loadFeed = useCallback(async () => {
    try {
      const res = await api.get(`/transactions/feed/${username}`);
      setFeed(res.data);
    } catch {/* ignore */}
  }, [username]);

  useEffect(() => {
    loadCreator();
    loadFeed();
    // Polling for real-time feed updates every 8s
    const iv = setInterval(loadFeed, 8000);
    return () => clearInterval(iv);
  }, [loadCreator, loadFeed]);

  // Load leaderboard
  useEffect(() => {
    if (!data) return;
    const loadLeaderboard = async () => {
      try {
        // We need the overlay token — use a public leaderboard endpoint
        const res = await api.get(`/transactions/leaderboard/${username}?limit=5`);
        setLeaderboard(res.data || []);
      } catch { /* ignore */ }
    };
    loadLeaderboard();
  }, [data, username]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-4xl mx-auto px-4 py-16">
          <LoadingVideo text="Memuat profil..." />
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="mb-8 border-4 border-foreground bg-white shadow-brutal-lg overflow-hidden max-w-xs mx-auto">
            <img src="/notfound-creator.png" alt="Not Found" className="w-full h-auto" />
          </div>
          <h1 className="font-display text-3xl font-bold mb-4" data-testid="creator-not-found">{error || 'Kreator tidak ditemukan'}</h1>
          <p className="text-muted-foreground mb-8 text-sm italic">Username yang Anda cari tidak dapat ditemukan atau telah dinonaktifkan.</p>
          <Link to="/" className="inline-block border-2 border-foreground bg-primary text-white px-8 py-3 font-bold uppercase tracking-wide shadow-brutal hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all">
            Kembali ke Beranda
          </Link>
        </main>
      </div>
    );
  }

  const { creator, units, goals, tts_info } = data;

  const linkify = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+|[a-zA-Z0-9.-]+\.[a-z]{2,}(\/[^\s]*)?)/g;
    return text.split(urlRegex).map((part, i) => {
      if (part && part.match(urlRegex)) {
        const url = part.startsWith('http') ? part : `https://${part}`;
        return <a key={i} href={url} target="_blank" rel="noreferrer" className="text-primary font-bold hover:underline break-all">{part}</a>;
      }
      return part;
    });
  };

  const getInitial = (name) => name ? name.charAt(0).toUpperCase() : '?';

  const openTip = (unitId) => {
    setSelectedUnit(unitId || null);
    setTipOpen(true);
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <Navbar />

      {/* NEW PREMIUM HERO BANNER */}
      <div 
        className="w-full h-48 md:h-64 border-b-2 border-foreground bg-secondary/20 relative"
        style={{
          backgroundImage: creator.cover_image ? `url(${resolveFileUrl(creator.cover_image)})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/5" />
        {/* Floating Profile Picture */}
        <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 md:left-12 md:translate-x-0 lg:left-24">
          <div className="w-32 h-32 md:w-40 md:h-40 border-4 border-foreground bg-white shadow-brutal-lg overflow-hidden flex items-center justify-center">
            {creator.picture ? (
              <img 
                src={resolveFileUrl(creator.picture)} 
                alt={creator.name} 
                className="w-full h-full object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = ''; e.target.parentElement.innerHTML = `<span class="text-4xl font-black">${getInitial(creator.name)}</span>`; }}
              />
            ) : (
              <span className="text-5xl font-black">{getInitial(creator.name)}</span>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 md:px-8 pt-20 md:pt-24 grid md:grid-cols-3 gap-8" data-testid="creator-public-page">
        {/* Left: Profile & units */}
        <div className="md:col-span-2 space-y-6">
          <div className="border-2 border-foreground bg-white shadow-brutal-md p-6 md:p-8" data-testid="creator-profile-card">
            <div className="flex flex-col gap-4">
              <div>
                <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight" data-testid="creator-name">{creator.name}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-muted-foreground font-mono text-sm" data-testid="creator-username">@{creator.username}</p>
                  {creator.is_first_mover && (
                    <span className="bg-accent border-2 border-foreground px-2 py-0.5 text-[10px] font-black uppercase shadow-[2px_2px_0px_0px_#09090B] flex items-center gap-1" title="Early Adopter Traktirkan">
                      🚀 First Mover
                    </span>
                  )}
                  {creator.auto_moderation && (
                    <span className="bg-primary text-white border-2 border-foreground px-2 py-0.5 text-[10px] font-black uppercase shadow-[2px_2px_0px_0px_#09090B] flex items-center gap-1" title="Protected by AI Protection System (Anti-Judol)">
                      <ShieldCheck size={10} weight="bold" /> AI Protected
                    </span>
                  )}
                </div>
                
                <div className="mt-5 text-sm md:text-base leading-relaxed whitespace-pre-wrap" data-testid="creator-bio">
                  {linkify(creator.bio)}
                </div>

                {creator.links && creator.links.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-6">
                    {creator.links.map((l, i) => (
                      <a
                        key={i}
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 border-2 border-foreground bg-primary text-white px-4 py-2 text-xs font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px]"
                        data-testid={`creator-link-${i}`}
                      >
                        <LinkIcon size={12} weight="bold" /> {l.label || 'Link'}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Units */}
          {units && units.length > 0 ? (
            <div className="border-2 border-foreground bg-white shadow-brutal-md p-6 md:p-8">
              <p className="text-xs uppercase tracking-[0.3em] font-bold mb-1">Traktir</p>
              <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mb-5">Pilih Dukunganmu</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {units.map((u) => (
                  <button
                    key={u.unit_id}
                    onClick={() => openTip(u.unit_id)}
                    className="border-2 border-foreground bg-secondary p-4 text-left shadow-brutal-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                    data-testid={`unit-card-${u.unit_id}`}
                  >
                    <div className="text-3xl mb-2">{u.emoji || '☕'}</div>
                    <p className="font-bold">{u.name}</p>
                    <p className="font-display text-lg font-bold">{formatRupiah(u.price)}</p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => openTip(null)}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 border-2 border-foreground bg-primary text-white px-5 py-3 font-bold uppercase tracking-wide shadow-brutal hover:shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                data-testid="traktir-now-btn"
              >
                <Heart size={18} weight="fill" /> Traktir Sekarang <ArrowRight size={18} weight="bold" />
              </button>
            </div>
          ) : (
            <div className="border-2 border-foreground bg-muted p-8 text-center">
              <p className="text-muted-foreground">Kreator belum menambahkan unit traktiran.</p>
            </div>
          )}

          {/* Goals */}
          {goals && goals.length > 0 && (
            <div className="space-y-4">
              {goals.map((g) => {
                const pct = Math.min(100, Math.round((g.current_amount / g.target_amount) * 100)) || 0;
                return (
                  <div key={g.goal_id} className="border-2 border-foreground bg-white shadow-brutal-md p-6" data-testid={`goal-card-${g.goal_id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Target size={18} weight="bold" />
                      <p className="text-xs uppercase tracking-widest font-bold">Goal</p>
                    </div>
                    <h3 className="font-display text-xl font-bold">{g.title}</h3>
                    {g.description && <p className="text-sm text-muted-foreground mb-3">{g.description}</p>}
                    <div className="h-5 border-2 border-foreground bg-muted overflow-hidden mt-2">
                      <div className="h-full bg-primary border-r-2 border-foreground transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex justify-between mt-2 text-sm font-bold">
                      <span>{formatRupiah(g.current_amount)}</span>
                      <span>{formatRupiah(g.target_amount)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Supporter feed + Leaderboard */}
        <aside className="md:col-span-1 space-y-6">
          {/* Mini Leaderboard */}
          {leaderboard.length > 0 && (
            <div className="border-2 border-foreground bg-white shadow-brutal-md" data-testid="mini-leaderboard">
              <div className="border-b-2 border-foreground bg-accent p-4">
                <div className="flex items-center gap-2">
                  <Trophy size={18} weight="bold" />
                  <h3 className="font-display text-lg font-bold">Top Supporters</h3>
                </div>
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-60 mt-0.5">Donasi terbanyak naik ke atas!</p>
              </div>
              <div className="p-4 space-y-2">
                {leaderboard.map((s, i) => (
                  <div key={i} className={`flex items-center gap-3 p-2 border-2 border-foreground ${
                    i === 0 ? 'bg-accent/40' : i === 1 ? 'bg-accent/20' : i === 2 ? 'bg-accent/10' : 'bg-white'
                  }`}>
                    <span className={`w-7 h-7 flex items-center justify-center border-2 border-foreground font-black text-xs ${
                      i === 0 ? 'bg-primary text-white' : 'bg-white'
                    }`}>{i + 1}</span>
                    <span className="text-sm font-bold flex-1 truncate">{s.name}</span>
                    <span className="text-xs font-bold whitespace-nowrap">{formatRupiah(s.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Supporter Feed */}
          <div className="sticky top-20 border-2 border-foreground bg-white shadow-brutal-md" data-testid="supporter-feed">
            <div className="border-b-2 border-foreground bg-foreground text-background p-4">
              <p className="text-xs uppercase tracking-[0.3em] font-bold opacity-80">Supporter</p>
              <h3 className="font-display text-xl font-bold">Pendukung Terbaru</h3>
            </div>
            <div className="max-h-[600px] overflow-y-auto p-4 space-y-3">
              {feed.length === 0 ? (
                <div className="text-center py-10">
                  <Heart size={32} weight="duotone" className="mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Jadilah pendukung pertama!</p>
                </div>
              ) : (
                feed.map((t) => (
                  <div key={t.order_id} className="border-2 border-foreground bg-background p-3 animate-slide-in-left" data-testid={`feed-item-${t.order_id}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{t.unit_emoji || '☕'}</span>
                      <p className="font-bold text-sm flex-1 truncate">{t.supporter_name}</p>
                      <span className="text-xs font-bold whitespace-nowrap">
                        {t.unit_name === 'Dana' ? `${t.unit_name} (${formatRupiah(t.amount)})` : `${t.qty}×${t.unit_name}`}
                      </span>
                    </div>
                    {t.message && <p className="mt-2 text-sm italic">"{t.message}"</p>}
                  </div>
                ))
              )}
            </div>
          </div>
        </aside>
      </main>

      {units && units.length > 0 && (
        <TipModal
          open={tipOpen}
          onOpenChange={setTipOpen}
          creator={creator}
          units={units}
          selectedUnitId={selectedUnit}
          ttsInfo={tts_info}
          mediaInfo={data.media_info}
        />
      )}
    </div>
  );
}
